import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, Segment } from 'semantic-ui-react'
import { DeleteOptionsProps } from '@cbgms/components/widgets/Knock/KnockWidget'
import { useDispatch, useSelector } from 'react-redux'
import isFunction from 'lodash/fp/isFunction'
import { IPaginatedRequest } from '@cbgms/api/request'
import DataGrid, { DataGridProps } from '@cbgms/components/grid/DataGrid'
import { IBodyRow, IColumn } from '@cbgms/components/grid/Types'
import ExportButton from './ExportButton'
import { RequestContextProvider } from '@cbgms/components/grid/Contexts/RequestContext'
import { DeleteResponseToMessageMapper, IDeleteModalContainerProps } from '@cbgms/components/form/Dialogs/DeleteModalContainer'
import { getSettings, removeSettings, setSettings } from '@cbgms/components/grid/state/actions'
import { useListSettings } from '@cbgms/components/grid/Hooks/use-list-settings'
import SearchHistoryButton from './SearchHistoryButton'
import { useListTranslators } from './ListWrapperUtils/hooks/use-list-translators'
import { useColumnsFromFields } from './ListWrapperUtils/hooks/use-columns-from-fields'
import { useListSettingsProps } from './ListWrapperUtils/hooks/use-list-settings-props'
import { ActionComponentFn, RefreshLoadDataFunc } from './ListWrapperUtils/hooks/types'
import { Button, Grid } from '@mui/material'
import { useListHeightOffset } from './ListWrapperUtils/ListHeightOffsetContext'
import { useGridV1Context } from '../grid/Contexts/GridV1Context'

type LoadAction = (params: IPaginatedRequest) => any

type exposedKeys =
  | 'enums'
  | 'heightOffset'
  | 'noDataPlaceholder'
  | 'onColumnMap'
  | 'onDataLoaderInitialized'
  | 'onRowMap'
  | 'rowHeight'
  | 'customCellRendererMap'
  | 'predefinedSearch'
  | 'hideColumnSettings'
  | 'onRowClassName'
  | 'useVinUnmask'

type ExposedGridProps = Pick<DataGridProps, exposedKeys>
interface IReducerWithEnums {
  enums: {
    readonly [enumName: string]: string[]
  }
}
export interface ListWrapperProps<T = any> extends Partial<ExposedGridProps> {
  /**
   * Hides or shows the UUID columns based on the provided value. By default this is `true`.
   */
  hideUUIDColumns?: boolean

  /**
   * Base key used for translations in the grid
   */
  translationKey: string

  /**
   * The plain action creator that gets called to load data, this action creator is automatically wrapped in dispatch
   *
   * @param params Params that get send along to the API
   */
  loadAction: LoadAction

  /**
   * Additional actions that render pinned to the right side.
   */
  actions?: ActionComponentFn<T>

  /**
   * Tab items to change the list content
   *
   * @param props All props that are passed to the ListWrapper
   * @param refreshLoadData Reloads all data when called
   */
  tabItems?: JSX.Element | ((props: any, refreshLoadData: RefreshLoadDataFunc) => JSX.Element)

  /**
   *  full width tabs
   * @param props All props that are passed to the ListWrapper
   * @param refreshLoadData Reloads all data when called
   */
  fullWidthTabItems?: JSX.Element | ((props: any, refreshLoadData: RefreshLoadDataFunc) => JSX.Element)
  /**
   * Additional menu items that get added to the top menu between the ADD button and menu items on the right
   *
   * @param props All props that are passed to the ListWrapper
   * @param refreshLoadData Reloads all data when called
   */
  middleMenuItems?: JSX.Element | ((props: any, refreshLoadData: RefreshLoadDataFunc) => JSX.Element)

  /**
   * Additional menu items that get added to the top menu on the right
   *
   * @param props All props that are passed to the ListWrapper
   * @param refreshLoadData Reloads all data when called
   */
  menuItems?: JSX.Element | ((props: any, refreshLoadData: RefreshLoadDataFunc) => JSX.Element)

  /**
   * Providing `columns` allows you to pass in additional columns.
   * For example required columns or columns of which
   * the data should be present but not necessarily visible.
   */
  columns?: IColumn[]

  /**
   * Called when the onDownload is clicked
   */
  onDownload?: (selectedTemplate: string) => void

  /**
   * Optional property to handle wholesaler form modal configurations.
   * Can be a boolean or an object containing a template list.
   */
  wholesalerFormModal?: boolean | { templateList: any[] }

  /**
   * Called when the add button is clicked, automatically displays an add button when this prop is present
   */
  onAdd?: () => void

  /**
   * Called when the view button is clicked, automatically adds a view button to every row when present
   *
   * If there is no onDoubleClick callback. onView will be used as a replacement
   *
   * @param row The selected row
   */
  onView?: (row: T) => void

  /**
   * Called when the edit button is clicked, automatically adds an edit button to every row when present
   *
   * If there is no onDoubleClick or onView callback. onEdit will be used as a replacement
   *
   * @param row The selected row
   */

  onEdit?: (row: T) => void

  /**
   * Called when the menu button is clicked, automatically adds an menu button to every row when present
   *
   * If there is no onDoubleClick or onView callback. onMenu will be used as a replacement
   *
   * @param row The selected row
   */

  onMenu?: (e: any, row: T) => void

  /**
   * Called when the menu button is clicked, automatically adds an send button to every row when present
   *
   * If there is no onDoubleClick or onView callback. onMenu will be used as a replacement
   *
   * @param row The selected row
   */

  onPendingLog?: (event: React.SyntheticEvent, row: T) => void

  /**
   * Called when the menu button is clicked, automatically adds an send button to every row when present
   *
   * If there is no onDoubleClick or onView callback. onMenu will be used as a replacement
   *
   * @param row The selected row
   */

  onPopupOpened?: (row: T) => void

  /**
   * Called when the menu button is clicked, automatically adds an delete button to every row when present
   *
   * If there is no onDoubleClick or onView callback. onMenu will be used as a replacement
   *
   * @param row The selected row
   */

  onPendingDelete?: (event: React.SyntheticEvent, row: T) => void

  /**
   * Called when the the row is double clicked
   *
   * If there is no onDoubleClick callback. onView or onEdit will be used as a replacement
   *
   * @param row The selected row
   */
  onDoubleClick?: (row: T) => void

  /**
   * Called when a row is hovered
   * @param row The hovered row
   * @returns JSX to render in the popup
   */
  onHoverPopup?: (row: T) => JSX.Element

  /**
   * Handler for the delete modal, called when the delete button is clicked. Automatically adds a delete button to every row when present
   * The data in the grid will automatically be reloaded after the removal is succesful
   *
   * @param row The selected row
   * @param dryRun Whether the delete is being ran in dryrun mode or not
   * @param reason The selected delete reason from the delete modal
   */
  onDelete?: (row: T, dryRun: boolean, reason?: string) => Promise<any>

  /**
   * Delete reasons to be passed to the delete modal
   */
  deleteReasons?: DeleteOptionsProps[]

  /**
   * The module scope used to retrieve the grid settings e.g.
   * `vehicles`, `accounts`, `proposals`.
   * Check the API documentation for all available modules
   * https://gms-api.develop.nl.carsys.online/docs/settings/global#GET-settings-global-list-module-type
   */
  scope: string

  /** Optional file name prefix for exports. Defaults to `scope`. */
  exportFileName?: string

  /** Enables dry run skipping when deleting a row. */
  skipDryRun?: boolean

  /** Enables multiple links if dryRun is not false and rejections have multiple UUIDs. */
  showMultiple?: boolean

  /**
   * The type of the list used to retrieve the settings e.g.
   * `list`, `list-with-related`, `portal-list-with-related`.
   * Check the API documentation for all available types
   * https://gms-api.develop.nl.carsys.online/docs/settings/global#GET-settings-global-list-module-type
   * */
  type?: string

  /** Shows or hides the export button in the menu. */
  hideExport?: boolean

  /** Shows or hides the default menu items in the menu. */
  hideDefaultMenuItems?: boolean

  /** The total width of the right action column as a number in pixels */
  actionColumnWidth?: number

  /**
   * UseApiSearchSettings: when this is true, it will use the api values for sorting and filtering
   * otherwise when settings are loaded the search/sort is reset.
   */
  UseApiSearchSettings?: boolean

  /**
   * Additional actions that render pinned to the left side.
   */
  startActions?: ActionComponentFn<T>

  /**
   * Additional, custom header for startActions column
   */
  startActionsHeader?: () => JSX.Element
  hideActions?: boolean
}

// IOverrideProps are props that are supposed to be injected by the the application
export interface IOverrideProps {
  /** Action to dispatch instead of the default settings load action */
  getSettings?: typeof getSettings

  /** Action to dispatch instead of the default settings store action */
  storeSettings?: typeof setSettings

  /** Action to dispatch instead of the default settings remove action */
  removeSettings?: typeof removeSettings

  mapRejectionToMessage?: DeleteResponseToMessageMapper

  getUserID?: () => string

  DefaultMenuItemComponent: React.ComponentType<{ right?: boolean }>

  DeleteModalContainer: React.ComponentType<DeleteModalProps>
}

export interface DeleteModalProps extends Omit<IDeleteModalContainerProps, 'mapApiResponseToMessage' | 'item' | 'isPortal'> {
  translationKey: string
  mapRejectionToMessage?: IDeleteModalContainerProps['mapApiResponseToMessage'] // This can be removed when ListWrapper is refactored
}

export function ListWrapper<T = any>(props: ListWrapperProps<T> & IOverrideProps) {
  const [showModal, setShowModal] = useState(false)
  const [selectedRowForDeletion, setSelectedRowForDeletion] = useState<T | null>(null)
  const [refreshLoadData, setRefreshLoadData] = useState<() => Promise<void>>(() => async () => {})
  const { t } = useTranslation()
  const { topbarHeightPx, margin, setMargin } = useGridV1Context()
  const dispatch = useDispatch()

  const showDeleteModal = useCallback(
    (row: T) => {
      setSelectedRowForDeletion(row)
      setShowModal(true)
    },
    [setSelectedRowForDeletion, setShowModal]
  )

  const closeModal = useCallback(() => {
    setShowModal(false)
  }, [setShowModal])

  const {
    hideUUIDColumns = true,
    actionColumnWidth,
    columns,
    deleteReasons,
    heightOffset: heightOffsetProp,
    loadAction,
    mapRejectionToMessage, // used by the support list wrapper
    onAdd,
    onColumnMap, // destructed to prevent overriding when spreading rest props
    onDataLoaderInitialized, // destructed to prevent overriding when spreading rest props
    onDelete,
    onDoubleClick, // destructed to prevent overriding when spreading rest props
    onEdit,
    onRowMap, // destructed to prevent overriding when spreading rest props
    onView,
    scope,
    exportFileName,
    hideExport = false,
    onDownload,
    hideDefaultMenuItems = false,
    skipDryRun,
    translationKey,
    startActions,
    tabItems,
    fullWidthTabItems,
    middleMenuItems,
    menuItems: rightMenuItems,
    DefaultMenuItemComponent: MenuItem,
    DeleteModalContainer,
    ...rest
  } = props

  const enums = useSelector((state: IReducerWithEnums) => state.enums)
  const connectedLoadAction = useCallback(request => dispatch(loadAction(request)), [loadAction, dispatch])

  const { translateKey, translateEnums, translateColumns } = useListTranslators(props)

  const combinedColumns = useColumnsFromFields<T>({
    ...props,
    onShowDeleteModal: showDeleteModal,
    refreshFunc: refreshLoadData
  })

  const wrappedOnDoubleClick = useCallback(
    (row: IBodyRow) => {
      if (onDoubleClick) return onDoubleClick(row.data)
      if (onView) return onView(row.data)
      if (onEdit) return onEdit(row.data)
    },
    [onView, onEdit, onDoubleClick]
  )

  const onWrappedDataLoaderInitialized = useCallback(
    (refreshData: () => Promise<void>) => {
      setRefreshLoadData(() => refreshData)
      if (props.onDataLoaderInitialized && refreshData) {
        props.onDataLoaderInitialized(refreshData)
      }
    },
    [props.onDataLoaderInitialized, setRefreshLoadData]
  )

  const handleRemove = useCallback(
    async (dryRun: boolean, reason?: string) => {
      if (!selectedRowForDeletion) return
      if (!props.onDelete) return

      const data = await props.onDelete(selectedRowForDeletion, dryRun, reason)
      if (!dryRun && refreshLoadData) {
        await refreshLoadData()
      }
      return data
    },
    [props.onDelete, refreshLoadData, selectedRowForDeletion]
  )

  const listSettingsProps = useListSettingsProps({ ...props, columns: combinedColumns })
  const {
    headers,
    onColumnMove,
    onColumnResize,
    onFilterChange,
    onResetFilters,
    onSettingAdd,
    onSettingRemove,
    onSettingsReset,
    predefinedSearch,
    isSettingsLoading,
    searchHistory,
    setActiveFilters,
    handlePageChange,
    handleLimitChange,
    setPaginationProperty,
    getPaginationProperty
  } = useListSettings(listSettingsProps)

  const rightMenu = useMemo(
    () => (rightMenuItems && isFunction(rightMenuItems) ? rightMenuItems(props, refreshLoadData) : rightMenuItems),
    [rightMenuItems, refreshLoadData]
  )

  const middleMenu = useMemo(
    () => (middleMenuItems && isFunction(middleMenuItems) ? middleMenuItems(props, refreshLoadData) : middleMenuItems),
    [middleMenuItems, refreshLoadData]
  )

  const tabs = useMemo(
    () => tabItems && <Menu.Menu position='left'>{isFunction(tabItems) ? tabItems(props, refreshLoadData) : tabItems}</Menu.Menu>,
    [tabItems, refreshLoadData]
  )

  const fullWidthTabs = useMemo(
    () =>
      fullWidthTabItems && (
        <Menu.Menu position='left' style={{ width: '-webkit-fill-available' }}>
          {isFunction(fullWidthTabItems) ? fullWidthTabItems(props, refreshLoadData) : fullWidthTabItems}
        </Menu.Menu>
      ),
    [fullWidthTabItems, refreshLoadData]
  )

  const gridColumns = headers.filter(header => header.visible).map(header => header.key)
  const contextHeightOffset = useListHeightOffset()
  useEffect(() => {
    setMargin(16) // margin here in px based on mt={1} mb={1} on grid
  }, [])
  const additionalSpace = margin ? topbarHeightPx - margin : 49 // fallback to old hardcoded setting
  const heightOffset =
    (heightOffsetProp ?? contextHeightOffset) + additionalSpace /* additional space for top menu */ + (tabs ? additionalSpace - 8 : 0)

  return (
    <RequestContextProvider>
      {showModal && (
        <DeleteModalContainer
          showModal={showModal}
          closeModal={closeModal}
          translationKey={translationKey}
          handleRemove={handleRemove}
          deleteReasons={deleteReasons}
          mapRejectionToMessage={mapRejectionToMessage}
          skipDryRun={skipDryRun}
          showMultiple={props.showMultiple ?? false}
        />
      )}
      <Grid container justifyContent='space-between' alignItems='center' mt={1} mb={1}>
        <Grid item container width='auto' alignItems='center'>
          {onAdd && (
            <MenuItem>
              <Button variant='contained' onClick={onAdd} data-testid='add'>
                {t(`global.add_${translationKey}`)}
              </Button>
            </MenuItem>
          )}
          {middleMenu}
        </Grid>
        <Grid item container width='auto' alignItems='center'>
          {rightMenu}
          {!hideDefaultMenuItems && (
            <>
              {!hideExport && (
                <MenuItem right>
                  <ExportButton
                    scope={exportFileName || scope}
                    gridColumns={gridColumns}
                    loadAction={connectedLoadAction}
                    wholesalerFormModal={props.wholesalerFormModal}
                    onDownload={onDownload}
                  />
                </MenuItem>
              )}
              <MenuItem right>
                <SearchHistoryButton
                  searchHistory={searchHistory}
                  columnNameTranslator={translateKey}
                  columns={headers}
                  onItemClick={setActiveFilters}
                />
              </MenuItem>
            </>
          )}
        </Grid>
      </Grid>
      {tabs && (
        <Menu style={{ marginTop: 0 }} attached='top'>
          {tabs}
        </Menu>
      )}
      {fullWidthTabs && (
        <Menu style={{ marginTop: 0, width: '-webkit-fill-available' }} attached='top'>
          {fullWidthTabs}
        </Menu>
      )}

      {isSettingsLoading ? (
        <Segment attached loading placeholder />
      ) : (
        <DataGrid
          predefinedSearch={predefinedSearch}
          hideUUIDColumns={hideUUIDColumns}
          attached
          columns={combinedColumns}
          dataLoader={connectedLoadAction}
          enums={enums}
          heightOffset={heightOffset}
          onColumnMap={translateColumns}
          onRowMap={translateEnums}
          onSettingsDisplay={translateKey}
          onDoubleClick={wrappedOnDoubleClick}
          onDataLoaderInitialized={onWrappedDataLoaderInitialized}
          headers={headers}
          onColumnMove={onColumnMove}
          onColumnResize={onColumnResize}
          onFilterChange={onFilterChange}
          onResetFilters={onResetFilters}
          onSettingAdd={onSettingAdd}
          onSettingRemove={onSettingRemove}
          onSettingsReset={onSettingsReset}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          setPaginationProperty={setPaginationProperty}
          getPaginationProperty={getPaginationProperty}
          {...rest}
        />
      )}
    </RequestContextProvider>
  )
}

export default ListWrapper
